import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import debounce from 'lodash/debounce';
import api from '../api';
import { clearResults as clearResultsAction } from '../actions/search';
import {
  searchResultsSelector,
  searchGetLoadingSelector
} from '../selectors/search';
import { Search as SearchIcon } from './icons';
import { formatContentForReact } from './globalHelpers';

import './Search.scss';

const Search = ({
  searchGet = () => {},
  searchResults = [],
  searchLoading = false,
  menuClose = null,
  clearResults = () => {},
}) => {
  const navigate = useNavigate();
  const [selection, setSelection] = useState(-1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showResults, setShowResults] = useState(false);

  // Debounce logic to prevent API spam
  const debouncedSearch = useMemo(
    () => debounce((term) => {
      if (term && term.length > 2) {
        searchGet(term);
        setShowResults(true);
      }
    }, 250),
    [searchGet]
  );

  useEffect(() => {
    debouncedSearch(searchTerm);
    return () => debouncedSearch.cancel();
  }, [searchTerm, debouncedSearch]);

  const handleResultClick = () => {
    if (menuClose) menuClose();
    setShowResults(false);
    setSearchTerm('');
  };

  const handleKeyPress = (event) => {
    const numberOfResults = searchResults.length;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelection(prev => (prev < numberOfResults - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelection(prev => (prev > -1 ? prev - 1 : prev));
        break;
      case 'Escape':
        setShowResults(false);
        break;
      default:
        break;
    }
  };

  const handleOnFocus = () => {
    if (searchTerm.length < 3) {
      clearResults();
      setSelection(-1);
    }
    setShowResults(true);
  };

  const handleOnSubmit = (event) => {
    event.preventDefault();
    if (selection !== -1 && searchResults[selection]) {
      const choice = searchResults[selection];
      const target = choice.path || choice.link;
      if (target) navigate(target);
      setShowResults(false);
      setSearchTerm('');
    }
  };

  const handleOnBlur = (e) => {
    // Timeout allows the Link click to register before the results div vanishes
    setTimeout(() => {
      if (!e.relatedTarget?.className?.includes('form-search')) {
        setShowResults(false);
      }
    }, 250);
  };

  return (
    <form onSubmit={handleOnSubmit} name="searchForm" autoComplete="off">
      <div className="form-search">
        <input
          className="form-search__input"
          name="searchQuery"
          type="search"
          onChange={(e) => setSearchTerm(e.target.value)}
          onBlur={handleOnBlur}
          onFocus={handleOnFocus}
          value={searchTerm}
          placeholder="Search..."
          onKeyDown={handleKeyPress}
        />
        <SearchIcon width="32" height="32" viewBox="0 0 32 32" />
        
        {showResults && (
          <div className="form-search__results-wrapper">
            {searchResults.length > 0 ? (
              <ul className="form-search__results">
                {searchResults.map((result, index) => (
                  <li key={`${result.title}-${index}`} className="form-search__result">
                    <Link
                      to={result.link || '#'}
                      onClick={handleResultClick}
                      className={`form-search__result-link ${
                        index === selection ? 'selected' : ''
                      }`}
                    >
                      {formatContentForReact(result.title)}
                      {result.subtitle && <span className="subtitle">{formatContentForReact(result.subtitle)}</span>}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              searchLoading && searchTerm.length > 2 && (
                <ul className="form-search__results">
                  <li className="form-search__result">Loading ...</li>
                </ul>
              )
            )}
          </div>
        )}
      </div>
    </form>
  );
};

Search.propTypes = {
  clearResults: PropTypes.func,
  searchResults: PropTypes.arrayOf(PropTypes.shape()),
  searchGet: PropTypes.func,
  searchLoading: PropTypes.bool,
  menuClose: PropTypes.func
};

const mapStateToProps = state => ({
  searchResults: searchResultsSelector(state),
  searchLoading: searchGetLoadingSelector(state)
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      searchGet: term => api.actions.searchGet({ term }),
      clearResults: clearResultsAction
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(Search);
